// src/components/Inventory/MyInventory.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Container,
  Heading,
  Input,
  ButtonBase,
  Select as NativeSelect,
  Error,
  Table,
  TableRow,
  TableData,
  FormContainer,
  FieldContainer,
  StockCounter,
  FlexContainer,
  ModalOverlay,
  ModalContent,
  RefreshButton,
  ListContainer
} from '../../styled/common';
import styled from 'styled-components';
import TechnicianWorkingStockDetailTable from '../Dashboard/TechnicianWorkingStockDetailTable';
import TechnicianFaultyStockDetailTable from '../Dashboard/TechnicianFaultyStockDetailTable';
import Select from 'react-select';


const customSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: '40px',
    fontSize: '0.95rem',
  }),
  menu: (base) => ({
    ...base,
    zIndex: 10,
  }),
};

const StyledSelect = styled(NativeSelect)`
  width: 100%;
  margin-bottom: 10px;
`;

const StyledInput = styled(Input)`
  width: 100%;
  margin-bottom: 10px;
  box-sizing: border-box;
`;

const SaveButton = styled(ButtonBase)`
  margin-top: 10px;
  width: 100%;
`;

const MyInventory = ({ user }) => {
  const [materials, setMaterials] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState({});
  const [quantityRequired, setQuantityRequired] = useState(0);
  const [selectedAction, setSelectedAction] = useState('');
  const [selectedState, setSelectedState] = useState(user.stateLocation);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [filteredInventoryItems, setFilteredInventoryItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [surplusStock, setSurplusStock] = useState(0);
  const [stockType, setStockType] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [materialOptions, setMaterialOptions] = useState([]);

  useEffect(() => {
    if (user?.role === 'tech' || user?.role === 'warehouseAdmin') {
      fetchMaterialsAndInventory();
    }
  }, [user]);

  useEffect(() => {
    filterInventoryItems();
  }, [searchTerm, inventoryItems]);

  useEffect(() => {
    if (selectedMaterial._id && selectedState) {
      fetchSurplusStock(selectedMaterial._id, selectedState);
    }
  }, [selectedMaterial, selectedState]);

  const fetchMaterialsAndInventory = async () => {
    try {
      const [materialsResponse, inventoryResponse] = await Promise.all([
        axios.get('/api/materials'),
        axios.get(`/api/inventory?tech_id=${user.id}`),
      ]);

      // 🔥 This is the new searchable select option list
      const options = materialsResponse.data.map((mat) => ({
        value: mat._id,
        label: `${mat.Material} - ${mat.MaterialDescription}`,
        raw: mat
      }));

      setMaterials(materialsResponse.data);
      setMaterialOptions(options); // <-- 👈 This is what you add
      setInventoryItems(inventoryResponse.data);
      setFilteredInventoryItems(inventoryResponse.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Please try again later.');
    }
  };

  const fetchSurplusStock = async (material, state) => {
    try {
      const response = await axios.get(`/api/warehouse-stock/?material=${material}&state=${state}`);
      const totalSurplusStock = response.data.reduce((acc, item) => acc + item.totalSurplusStock, 0);
      setSurplusStock(totalSurplusStock);
    } catch (error) {
      console.error('Error fetching surplus stock:', error.message);
      setError('Failed to retrieve surplus stock.');
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const filterInventoryItems = () => {
    const filtered = inventoryItems.filter((item) =>
      `${item.material_id.Material} - ${item.material_id.MaterialDescription}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    );
    setFilteredInventoryItems(filtered);
  };

  const handleActionChange = (e) => {
    const action = e.target.value;
    setSelectedAction(action);
    setSelectedState(action === 'Add Material' ? user.stateLocation : '');
  };

  const handleMaterialChange = (e) => {
    const selectedValue = e.target.value;
    const selectedMaterialData = materials.find(
      (material) => `${material.Material} - ${material.MaterialDescription}` === selectedValue
    );
    setSelectedMaterial(selectedMaterialData || {});
  };

  const handleSubmit = async () => {
    if (!selectedAction) {
      setError('Please select an action.');
      return;
    }

    if (!selectedMaterial._id) {
      setError('Please select a material.');
      return;
    }

    if (selectedAction === 'Add Material' && !selectedState) {
      setError('Please select a state warehouse.');
      return;
    }

    if (quantityRequired <= 0 || isNaN(quantityRequired)) {
      setError('Please enter a valid quantity greater than 0.');
      return;
    }  

    if (!comment.trim()) {
      setError('Please enter a comment for this action.');
      return;
    }

    const requestBody = {
      userId: user.id,
      materialId: selectedMaterial._id,
      quantityRequired: parseInt(quantityRequired, 10),
      comment: comment.trim()
    };

    try {
      let response;

      if (selectedAction === 'Add Spare to Warehouse') {
        if (user.role !== 'warehouseAdmin') {
          setError('Only warehouse admins can add spare stock to the warehouse.');
          return;
        }
        response = await axios.post('/api/salesorders/add-spare', {
          ...requestBody,
          addToInventory: false, // Adding to warehouse only
          stateLocation: user.stateLocation
        });
  
      } else if (selectedAction === 'Add Spare to My Inventory') {
        response = await axios.post('/api/salesorders/add-spare', {
          ...requestBody,
          addToInventory: true, // Adding to personal inventory
          stateLocation: user.stateLocation
        });

      } else if  (selectedAction === 'Add Material') {
        response = await axios.post('/api/warehouse-stock/check-and-fulfill-request', {
          ...requestBody,
          stateLocation: selectedState,
        });
      } else {
        requestBody.statusField = getStatusField(selectedAction);
        response = await axios.post('/api/inventory/check-and-fulfill-request', requestBody);
      }

      alert(response.data.message);
      setError(null);
      fetchMaterialsAndInventory();
      setComment('');
    } catch (error) {
      console.error('Error fulfilling stock:', error);
      const errorMessage = error.response?.data?.message || 'Failed to fulfill stock. Please try again.';
      alert(errorMessage);
      setError(errorMessage);
    }
  };

  const getStatusField = (action) => {
    switch (action) {
      case 'Return Material to Warehouse':
        return 'Return Local Status';
      case 'Report Faulty Material':
        return 'Fault Status';
      default:
        return 'Return Optus Status';
    }
  };

  const handleRowClick = (material, type = null) => {
    setSelectedMaterial(material);
    setStockType(type);
    setIsModalOpen(true);
  };

  const handleCloseDetail = () => {
    setSelectedMaterial({});
    setIsModalOpen(false);
  };

  const handleRefresh = async () => {
    try {
      await fetchMaterialsAndInventory();
      setSearchTerm(''); // Clear the search term
      setError(null);
    } catch (err) {
      console.error('Error refreshing inventory:', err);
      setError('Failed to refresh inventory. Please try again.');
    }
  };

  return (
    <Container>
      {(user?.role === 'tech' || user?.role === 'warehouseAdmin') && (
        <FlexContainer>
          {/* Left Container: Form */}
          <FormContainer style={{ flex: '1 1 100%', maxWidth: '100%', marginBottom: '20px' }}>
            <Heading>Update Inventory</Heading>
            {error && <Error>{error}</Error>}
            <FlexContainer
              direction="column"
              align="left"
              gap="20px"
              style={{
                marginBottom: '20px',
                padding: '10px',
                backgroundColor: '#f9f9f9',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                flexWrap: 'wrap',
              }}
            >
              <FieldContainer>
                <label htmlFor="actionSelect">Select Action</label>
                <StyledSelect id="actionSelect" value={selectedAction} onChange={handleActionChange}>
                  <option value="">Select Action</option>
                  <option value="Add Material">Add Material from Warehouse</option>
                  <option value="Report Faulty Material">Report Faulty Material</option>
                  <option value="Return Material to Warehouse">Return Material to Warehouse</option>
                  <option value="Return Faulty Material to Optus">Return Faulty Material to Optus</option>
                  {user?.role === 'warehouseAdmin' && <option value="Add Spare to Warehouse">Add Spare to Warehouse</option>}
                  <option value="Add Spare to My Inventory">Add Spare to My Inventory</option>
                </StyledSelect>
              </FieldContainer>
    
              <FieldContainer>
                <label>Select Material</label>
                <Select
                  styles={customSelectStyles}
                  options={materialOptions}
                  onChange={(opt) => setSelectedMaterial(opt?.raw)}
                  placeholder="Search or select material..."
                  isSearchable
                />
              </FieldContainer>
    
              {selectedAction === 'Add Material' && (
                <>
                  <FieldContainer>
                    <label htmlFor="stateSelect">Select State Warehouse</label>
                    <StyledSelect id="stateSelect" value={selectedState} onChange={(e) => setSelectedState(e.target.value)}>
                      <option value="">Select State</option>
                      <option value="NSW">NSW</option>
                      <option value="VIC">VIC</option>
                      <option value="QLD">QLD</option>
                      <option value="SA">SA</option>
                      <option value="WA">WA</option>
                      <option value="TAS">TAS</option>
                      <option value="ACT">ACT</option>
                      <option value="NT">NT</option>
                    </StyledSelect>
                  </FieldContainer>
    
                  {selectedMaterial._id && selectedState && (
                    <StockCounter stock={surplusStock}>
                      <div>Available Stock: {surplusStock} units</div>
                    </StockCounter>
                  )}
                </>
              )}
    
              <FieldContainer>
                <label htmlFor="quantityRequired">Quantity</label>
                <StyledInput
                  id="quantityRequired"
                  type="number"
                  placeholder="Enter required quantity"
                  value={quantityRequired}
                  min="1"
                  onChange={(e) => {
                    const value = Math.max(1, Number(e.target.value));
                    setQuantityRequired(value);
                  }}
                />
              </FieldContainer>

              <FieldContainer>
                <label htmlFor="commentBox">Comment <span style={{ color: 'red' }}>*</span></label>
                <StyledInput
                  id="commentBox"
                  type="text"
                  placeholder="Enter comment for this action"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </FieldContainer>
            </FlexContainer>
                
            <SaveButton variant="primary" onClick={handleSubmit}>
              Submit
            </SaveButton>
          </FormContainer>
  
          {/* Right Container: Inventory List */}
          <ListContainer style={{ flex: '1 1 100%', maxWidth: '100%' }}>
            <Heading>Inventory List</Heading>
            <FlexContainer
              direction="row"
              align="center"
              gap="20px"
              style={{
                marginBottom: '20px',
                padding: '10px',
                backgroundColor: '#f9f9f9',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                flexWrap: 'wrap',
              }}
            >
              <FieldContainer>
                <label htmlFor="searchInventory">Search Inventory:</label>
                <Input
                  id="searchInventory"
                  type="text"
                  placeholder="Search by Material"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </FieldContainer>
              
              <FieldContainer>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <RefreshButton onClick={handleRefresh} title="Refresh Inventory" />
                </div>
              </FieldContainer>
            </FlexContainer>
            
            <Table>
              <thead>
                <tr>
                  <th>Material</th>
                  <th>Working Qty</th>
                  <th>Faulty Qty</th>
                </tr>
              </thead>
              <tbody>
                {filteredInventoryItems.length > 0 ? (
                  filteredInventoryItems
                    .filter((item) => item.totalWorkingStockOnHand > 0 || item.totalFaultyStock > 0)
                    .map((item) => (
                      <TableRow key={item._id}>
                        <TableData data-label="Material">
                          {item.material_id.Material} - {item.material_id.MaterialDescription}
                        </TableData>
                        <TableData
                          data-label="Working Qty"
                          onClick={() => handleRowClick(item.material_id._id, 'working')}
                        >
                          {item.totalWorkingStockOnHand}
                        </TableData>
                        <TableData
                          data-label="Faulty Qty"
                          onClick={() => handleRowClick(item.material_id._id, 'faulty')}
                        >
                          {item.totalFaultyStock}
                        </TableData>
                      </TableRow>
                    ))
                ) : (
                  <tr>
                    <td colSpan="3">No inventory items available.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </ListContainer>
        </FlexContainer>
      )}
  
      {/* Modal */}
      {isModalOpen && (
        <ModalOverlay>
          <ModalContent>
            {selectedMaterial && (
              stockType === 'faulty' ? (
                <TechnicianFaultyStockDetailTable
                  user={user}
                  material={selectedMaterial}
                  tech={user.id}
                  onClose={handleCloseDetail}
                />
              ) : (
                <TechnicianWorkingStockDetailTable
                  user={user}
                  material={selectedMaterial}
                  tech={user.id}
                  onClose={handleCloseDetail}
                />
              )
            )}
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );   
};

export default MyInventory;
